import { SortCategory, MultipleChoiceExamExaminerData } from '../../../../../store/workspace/types';
import { selectSelectedApplicantsIds } from '../../../../../store/workspace/selectors';
import { getExaminerProfileThunk } from '../../../../../store/workspace/thunks';
import { examStatusTitle } from '../../../../../utils/generalHelpers';
import { useDispatch, useSelector } from '../../../../../store/store';
import { ForwardedRef, forwardRef, useEffect, useState } from 'react';
import CheckBox from '../../../../../components/CheckBox';
import { useNavigate } from 'react-router-dom';
import styles from './style.module.scss';
import {
    DangerIcon,
    ProfileIcon,
    MessageIcon,
    DocumentIcon,
    ChevronUpIcon,
    ChevronDownIcon,
} from '../../../../../assets/icons';
import {
    setExaminerReportData,
    setExaminerReportModal,
    setExaminerProfileModal,
    setSelectedApplicantsIds,
} from '../../../../../store/workspace/slice';

interface Props {
    selectable: boolean;
    jobId: string | undefined;
    examinersType: 'Qualified' | 'All';
    examinersInfo: MultipleChoiceExamExaminerData[];
};

const MultipleChoiceExamTable = forwardRef((props: Props, ref: ForwardedRef<HTMLDivElement>) => {
    const {
        jobId,
        selectable,
        examinersInfo,
        examinersType,
    } = props;

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const listCopy = examinersInfo ? [...examinersInfo] : []
    const selectedApplicantsIds = useSelector(selectSelectedApplicantsIds);
    listCopy.sort((a, b) => b.scores.score_overall - a.scores.score_overall);
    const qualifiedExaminers = listCopy.filter(i => i.scores.score_overall >= (i.scores.max_score / 2));
    const [tableInfo, setTableInfo] = useState(qualifiedExaminers);
    const [expandedCards, setExpandedCards] = useState<string[]>([]);

    const toggleCard = (userId: string) => {
        setExpandedCards(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    useEffect(() => {
        switch (examinersType) {
            case 'Qualified':
                setTableInfo(qualifiedExaminers);
                break;
            case 'All':
                setTableInfo(examinersInfo);
                break;
            default:
                return;
        }
    }, [examinersInfo, examinersType]);

    const sortTable = (cat: SortCategory, type: 'ascend' | 'descend') => {
        switch (type) {
            case 'ascend':
                listCopy.sort((a, b) => a.scores[cat] - b.scores[cat]);
                setTableInfo(listCopy);
                break;
            case 'descend':
                listCopy.sort((a, b) => b.scores[cat] - a.scores[cat]);
                setTableInfo(listCopy);
                break;
            default:
                return;
        }
    };

    const selectExaminer = (examinerId: string) => {
        const check = selectedApplicantsIds.some(i => i === examinerId);
        let list = [...selectedApplicantsIds];

        if (check) {
            list = list.filter(i => i !== examinerId);
        } else {
            list.push(examinerId);
        }

        dispatch(setSelectedApplicantsIds(list));
    };

    const selectAllExaminers = () => {
        const check = selectedApplicantsIds.length === examinersInfo.length;
        const newList = check ? [] : tableInfo.map(i => i.userId);
        dispatch(setSelectedApplicantsIds(newList));
    };

    const getExaminerProfile = (id: string) => {
        dispatch(getExaminerProfileThunk({ id, jobId }));
        dispatch(setExaminerProfileModal(true));
    };

    const openReportModal = (detail: MultipleChoiceExamExaminerData) => {
        dispatch(setExaminerReportData({
            status: detail.status,
            report: detail.feedback_report,
            fullName: `${detail.first_name} ${detail.family_name}`,
        }));

        dispatch(setExaminerReportModal(true));
    };

    return (
        <div className={styles.table} ref={ref}>
            <table>
                <thead>
                    <tr>
                        <th colSpan={3}>
                            {selectable && <div className={styles.select_all}>
                                <CheckBox
                                    onClick={selectAllExaminers}
                                    checked={tableInfo.length !== 0 && tableInfo.length === selectedApplicantsIds.length}
                                />
                                <span>Select All ({tableInfo.length})</span>
                            </div>}
                        </th>
                        <th colSpan={3}>
                            <span className={styles.head_section}>
                                Question levels
                            </span>
                        </th>
                        <th colSpan={3}>
                            <span className={styles.head_section}>
                                Question types
                            </span>
                        </th>
                        <th colSpan={3}></th>
                    </tr>
                    <tr>
                        <th>Name</th>
                        <th>Status</th>
                        <th>
                            <div className={styles.head_group}>
                                <div className={styles.head_group_sort}>
                                    <ChevronUpIcon onClick={() => sortTable('score_overall', 'descend')} />
                                    <ChevronDownIcon onClick={() => sortTable('score_overall', 'ascend')} />
                                </div>
                                <span>
                                    Grade
                                </span>
                            </div>
                        </th>
                        <th data-group-start="question-levels">
                            <div className={styles.head_group}>
                                <div className={styles.head_group_sort}>
                                    <ChevronUpIcon onClick={() => sortTable('score_easy_level', 'descend')} />
                                    <ChevronDownIcon onClick={() => sortTable('score_easy_level', 'ascend')} />
                                </div>
                                <span>
                                    Easy
                                </span>
                            </div>
                        </th>
                        <th data-group-middle="question-levels">
                            <div className={styles.head_group}>
                                <div className={styles.head_group_sort}>
                                    <ChevronUpIcon onClick={() => sortTable('score_medium_level', 'descend')} />
                                    <ChevronDownIcon onClick={() => sortTable('score_medium_level', 'ascend')} />
                                </div>
                                <span>
                                    Medium
                                </span>
                            </div>
                        </th>
                        <th data-group-end="question-levels">
                            <div className={styles.head_group}>
                                <div className={styles.head_group_sort}>
                                    <ChevronUpIcon onClick={() => sortTable('score_hard_level', 'descend')} />
                                    <ChevronDownIcon onClick={() => sortTable('score_hard_level', 'ascend')} />
                                </div>
                                <span>
                                    Hard
                                </span>
                            </div>
                        </th>
                        <th data-group-start="question-types">
                            <div className={styles.head_group}>
                                <div className={styles.head_group_sort}>
                                    <ChevronUpIcon onClick={() => sortTable('score_general', 'descend')} />
                                    <ChevronDownIcon onClick={() => sortTable('score_general', 'ascend')} />
                                </div>
                                <span>
                                    General
                                </span>
                            </div>
                        </th>
                        <th data-group-middle="question-types">
                            <div className={styles.head_group}>
                                <div className={styles.head_group_sort}>
                                    <ChevronUpIcon onClick={() => sortTable('score_technical', 'descend')} />
                                    <ChevronDownIcon onClick={() => sortTable('score_technical', 'ascend')} />
                                </div>
                                <span>
                                    Technical
                                </span>
                            </div>
                        </th>
                        <th data-group-end="question-types">
                            <div className={styles.head_group}>
                                <div className={styles.head_group_sort}>
                                    <ChevronUpIcon onClick={() => sortTable('score_coding', 'descend')} />
                                    <ChevronDownIcon onClick={() => sortTable('score_coding', 'ascend')} />
                                </div>
                                <span>
                                    Coding
                                </span>
                            </div>
                        </th>
                        <th data-noprint="true">Report</th>
                        <th data-noprint="true">Sheet &<br />feedback</th>
                        <th data-noprint="true">Info</th>
                    </tr>
                </thead>
                <tbody>
                    {tableInfo.length === 0 ? (<tr><td colSpan={9}>No data.</td></tr>) :
                        tableInfo.map(info => <tr key={info.userId}>
                            <td>
                                <div className={styles.examinerName}>
                                    {selectable && <CheckBox
                                        onClick={() => selectExaminer(info.userId)}
                                        checked={selectedApplicantsIds.some(i => i === info.userId)}
                                    />}
                                    <span>{info.first_name} {info.family_name}</span>
                                    {info.proctoring && <div className={styles.proctoring}>
                                        <DangerIcon />
                                        <span className={styles.proctoring_tooltip}>
                                            {info.first_name} {info.family_name}&nbsp;
                                            was out of the exam for&nbsp;
                                            {info.userInactiveTime ? parseFloat(info.userInactiveTime).toFixed(1) : 0} seconds.
                                        </span>
                                    </div>}
                                </div>
                            </td>
                            <td>
                                <span
                                    className={styles.status}
                                    data-status={info.status}
                                >
                                    {examStatusTitle(info.status)}
                                </span>
                            </td>
                            <td>{info.scores.score_overall}/{info.scores.max_score}</td>
                            <td data-group-start="question-levels">{info.scores.score_easy_level}/{info.scores.n_questions_easy}</td>
                            <td data-group-middle="question-levels">{info.scores.score_medium_level}/{info.scores.n_questions_medium}</td>
                            <td data-group-end="question-levels">{info.scores.score_hard_level}/{info.scores.n_questions_hard}</td>
                            <td data-group-start="question-types">{info.scores.score_general}/{info.scores.n_questions_general}</td>
                            <td data-group-middle="question-types">{info.scores.score_technical}/{info.scores.n_questions_technical}</td>
                            <td data-group-end="question-types">{info.scores.score_coding}/{info.scores.n_questions_coding}</td>
                            <td data-noprint="true"><MessageIcon onClick={() => openReportModal(info)} /></td>
                            <td data-noprint="true"><DocumentIcon onClick={() => navigate(`${info.userId}`)} /></td>
                            <td data-noprint="true"><ProfileIcon onClick={() => getExaminerProfile(info.userId)} /></td>
                        </tr>)
                    }
                </tbody>
            </table>

            <div className={styles.mobile_list}>
                {tableInfo.map(info => {
                    const isExpanded = expandedCards.includes(info.userId);
                    return (
                        <div className={styles.card}>
                            <div className={styles.card_header} onClick={() => toggleCard(info.userId)}>
                                <span>{info.first_name} {info.family_name}</span>
                                <ChevronDownIcon style={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }} />
                            </div>
                            {isExpanded && <div className={styles.card_body}>
                                <div className={styles.card_body_section}>
                                    <div className={styles.card_body_section_title}>
                                        <span>Status</span>
                                        <span className={styles.status} data-status={info.status}>
                                            {examStatusTitle(info.status)}
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.card_body_section}>
                                    <div className={styles.card_body_section_title}>
                                        <span>Grade</span>
                                        <span>{info.scores.score_overall}/{info.scores.max_score}</span>
                                    </div>
                                </div>
                                <div className={styles.card_body_section}>
                                    <div className={styles.card_body_section_title}>
                                        Question levels
                                    </div>
                                    <div className={styles.card_body_section_content}>
                                        <div className={styles.card_body_section_content_item}>
                                            <span>Easy</span>
                                            <span>{info.scores.score_easy_level}/{info.scores.n_questions_easy}</span>
                                        </div>
                                    </div>
                                    <div className={styles.card_body_section_content}>
                                        <div className={styles.card_body_section_content_item}>
                                            <span>Medium</span>
                                            <span>{info.scores.score_medium_level}/{info.scores.n_questions_medium}</span>
                                        </div>
                                    </div>
                                    <div className={styles.card_body_section_content}>
                                        <div className={styles.card_body_section_content_item}>
                                            <span>Hard</span>
                                            <span>{info.scores.score_hard_level}/{info.scores.n_questions_hard}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.card_body_section}>
                                    <div className={styles.card_body_section_title}>
                                        Question types
                                    </div>
                                    <div className={styles.card_body_section_content}>
                                        <div className={styles.card_body_section_content_item}>
                                            <span>General</span>
                                            <span>{info.scores.score_general}/{info.scores.n_questions_general}</span>
                                        </div>
                                    </div>
                                    <div className={styles.card_body_section_content}>
                                        <div className={styles.card_body_section_content_item}>
                                            <span>Technical</span>
                                            <span>{info.scores.score_technical}/{info.scores.n_questions_technical}</span>
                                        </div>
                                    </div>
                                    <div className={styles.card_body_section_content}>
                                        <div className={styles.card_body_section_content_item}>
                                            <span>Coding</span>
                                            <span>{info.scores.score_coding}/{info.scores.n_questions_coding}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>}
                        </div>)
                })}
            </div>
        </div>
    )
});

export default MultipleChoiceExamTable;
